import { gql } from '@apollo/client';
import { initializeApollo } from '~/shared/libs/apollo/apollo-provider';
import { runUploadBatches } from '~/universal/upload-batches';
import { directSessionChunkBytes } from '~/universal/direct-upload-chunks';

export type DirectSession = { sessionId: string; key: string; chunkBytes?: number; protocol?: string; maxParallelParts?: number };
const transportQuery = gql`query UploadTransport { uploadTransportQuery }`;
const initMutation = gql`mutation InitDirectUpload($files: [DirectUploadInput!]) {
  initDirectUploadBatchMutation(files: $files) { sessionId key status chunkBytes protocol maxParallelParts }
}`;
const signMutation = gql`mutation SignDirectPart($sessionId: String!, $partNumber: Int!, $checksum: String!) {
  signDirectUploadPartMutation(sessionId: $sessionId, partNumber: $partNumber, checksum: $checksum) { url fieldsJSON expiresIn }
}`;
const confirmMutation = gql`mutation ConfirmDirectPart($sessionId: String!, $partNumber: Int!) {
  confirmDirectUploadPartMutation(sessionId: $sessionId, partNumber: $partNumber) { nextPart confirmedPart }
}`;
const completeMutation = gql`mutation CompleteDirectUpload($sessionId: String!) {
  completeDirectUploadMutation(sessionId: $sessionId) { status location }
}`;
export async function getUploadTransport(): Promise<'backend' | 'presigned'> {
  const client = await initializeApollo();
  const response = await client.query<{ uploadTransportQuery: string }>({ query: transportQuery, fetchPolicy: 'network-only' });
  const mode = response.data?.uploadTransportQuery;
  if (mode !== 'backend' && mode !== 'presigned') throw new Error('Invalid upload transport configuration');
  return mode;
}
async function mutate(mutation: any, variables: any, field: string): Promise<any> {
  const client = await initializeApollo();
  const response = await client.mutate<Record<string, any>>({ mutation, variables,
    context: { headers: { 'Apollo-Require-Preflight': 'true' } } });
  const result = response.data?.[field];
  if (!result) throw new Error('Upload API returned no result');
  return result;
}
export async function initDirectBatch(files: any[]): Promise<DirectSession[]> {
  const sessions = await mutate(initMutation, { files }, 'initDirectUploadBatchMutation');
  if (!Array.isArray(sessions) || sessions.length !== files.length || sessions.some(s => !s.sessionId || !s.key)) {
    throw new Error('Could not initialize direct uploads');
  }
  sessions.forEach(session => directSessionChunkBytes(session));
  return sessions;
}
// POST remains bound to the signed policy, exact key, size and checksum.
function postPart(url: string, form: FormData, onProgress: (fraction: number) => void): Promise<{ status: number }> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', url);
    request.withCredentials = false;
    request.timeout = 180000;
    request.upload.onprogress = event => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => resolve({ status: request.status });
    request.onerror = () => reject(new Error('Could not send file part'));
    request.ontimeout = () => reject(new Error('File part upload timed out'));
    request.onabort = () => reject(new Error('File part upload cancelled'));
    request.send(form);
  });
}
export async function uploadDirectFile(file: File, session: DirectSession, onPart: () => void,
  onBytes?: (uploadedBytes: number) => void): Promise<string> {
  const chunkBytes = directSessionChunkBytes(session);
  const partCount = Math.ceil(file.size / chunkBytes);
  const parallel = session.protocol === 'parallel-v1' ? Math.min(4, session.maxParallelParts ?? 4) : 1;
  const sent = new Map<number, number>();
  const report = (number: number, bytes: number) => {
    sent.set(number, bytes);
    onBytes?.(Math.min(file.size, Array.from(sent.values()).reduce((sum, value) => sum + value, 0)));
  };
  await runUploadBatches(partCount, parallel, async (number) => {
    const chunk = file.slice((number - 1) * chunkBytes, Math.min(number * chunkBytes, file.size));
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', await chunk.arrayBuffer()));
    const checksum = btoa(Array.from(digest, byte => String.fromCharCode(byte)).join(''));
    let uploaded = false;
    // Fresh authorization for retries; checksum remains pinned and S3 key remains fixed.
    for (let attempt = 0; attempt < 3; attempt++) {
      const signed = await mutate(signMutation, { sessionId: session.sessionId, partNumber: number, checksum },
        'signDirectUploadPartMutation');
      const fields = JSON.parse(signed.fieldsJSON);
      const url = new URL(signed.url);
      if (url.protocol !== 'https:' || !url.hostname.endsWith('.amazonaws.com')) throw new Error('Invalid upload endpoint');
      const form = new FormData();
      for (const [name, value] of Object.entries(fields)) form.append(name, String(value));
      form.append('file', chunk, 'part.bin'); // S3 requires the file field last.
      try {
        const response = onBytes
          ? await postPart(url.toString(), form, fraction => report(number, chunk.size * fraction))
          : await fetch(url.toString(), { method: 'POST', body: form, credentials: 'omit',
              referrerPolicy: 'no-referrer', signal: AbortSignal.timeout(180000) });
        if (response.status === 201) { uploaded = true; break; }
        if (![403, 408, 429, 500, 502, 503, 504].includes(response.status)) {
          throw new Error(`S3 rejected part (${response.status})`);
        }
      } catch (error) {
        if (attempt === 2) throw error;
      }
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
    if (!uploaded) throw new Error('Could not upload file part');
    // Repeating confirmation is safe if the previous API response was lost.
    let confirmed = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const result = await mutate(confirmMutation, { sessionId: session.sessionId, partNumber: number },
          'confirmDirectUploadPartMutation');
        if (session.protocol === 'parallel-v1' ? result.confirmedPart !== number : result.nextPart !== number + 1) throw new Error('Unexpected upload state');
        confirmed = true; break;
      } catch (error) { if (attempt === 2) throw error; }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (!confirmed) throw new Error('Could not confirm upload part');
    report(number, chunk.size);
    onPart();
  });
  const done = await mutate(completeMutation, { sessionId: session.sessionId }, 'completeDirectUploadMutation');
  if (done.status !== 'COMPLETED' || !done.location) throw new Error('Could not complete upload');
  return done.location;
}

